import { MutableRefObject, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSurveyResultsFilter } from 'hooks/useSurveyResultsFilter';
import { isObjectEmpty } from 'helpers';
import {
	ICommentsAverage,
	IPointWithOffset,
	MAP_ZOOM_TYPE_INDEXES,
} from 'modules/admin-panel/pages/results/models';
import {
	CITY_DEFAULT_ZOOM,
	COUNTRY_DEFAULT_ZOOM,
	GLOBAL_DEFAULT_ZOOM,
	OFFICE_DEFAULT_ZOOM,
} from 'modules/admin-panel/pages/results/helpers/map/resultMapConfig';
import {
	isLocationChangedSelector,
	isLocationSearchForOfficeSelector,
	zoomTypeIndexSelector,
} from 'modules/admin-panel/pages/results/selectors/map.selectors';
import {
	resetIsLocationSearchForOffice,
	setIsLocationChanged,
} from 'modules/admin-panel/pages/results/map.actions';

interface MapEffectsParams {
	mapRef: MutableRefObject<google.maps.Map | undefined>;
	markerList: ICommentsAverage[] | null | undefined;
	setLastSelected: (item: ICommentsAverage) => void;
	panToGroupBounds: (mapPoints: ICommentsAverage[]) => void;
	zoomToLevel: (level: number, points: ICommentsAverage[]) => void;
	zoomSelectedOffice: (selectedItem: IPointWithOffset) => void;
}

export const useMapEffects = ({
	mapRef,
	markerList,
	setLastSelected,
	panToGroupBounds,
	zoomToLevel,
	zoomSelectedOffice,
}: MapEffectsParams) => {
	const dispatch = useDispatch();
	const { surveyFilter } = useSurveyResultsFilter();
	const isLocationSearchForOffice = useSelector(
		isLocationSearchForOfficeSelector,
	);
	const isLocationChanged = useSelector(isLocationChangedSelector);
	const zoomTypeIndex = useSelector(zoomTypeIndexSelector);

	// Autozooming for the map on the 'Choose survey' page
	useEffect(() => {
		if (isObjectEmpty(surveyFilter) && markerList?.length) {
			panToGroupBounds(markerList);
		}
	}, [surveyFilter, markerList]);

	// Autozooming for the map
	useEffect(() => {
		if (!markerList?.length) return;

		switch (zoomTypeIndex) {
			case MAP_ZOOM_TYPE_INDEXES.worldZoom:
				zoomToLevel(GLOBAL_DEFAULT_ZOOM, markerList);
				break;
			case MAP_ZOOM_TYPE_INDEXES.countryZoom:
				if (!markerList.every((m) => m.country === markerList[0].country))
					break;
				zoomToLevel(COUNTRY_DEFAULT_ZOOM, markerList);
				break;
			case MAP_ZOOM_TYPE_INDEXES.cityZoom:
				if (!markerList.some((m) => m.office)) break;
				zoomToLevel(CITY_DEFAULT_ZOOM, markerList);
				break;
			case MAP_ZOOM_TYPE_INDEXES.officeZoom:
				if (markerList.length > 1) break;
				zoomSelectedOffice(markerList[0]);
				break;
		}
	}, [markerList, zoomTypeIndex, zoomToLevel]);

	// Set selected item to the chosen one in the Location search field
	// Autozooming for office level after using Location search
	useEffect(() => {
		if (!isLocationChanged || !markerList?.length) return;

		setLastSelected(markerList[0]);
		dispatch(setIsLocationChanged(false));

		if (isLocationSearchForOffice) {
			zoomToLevel(OFFICE_DEFAULT_ZOOM, markerList);
			dispatch(resetIsLocationSearchForOffice());
		}
	}, [
		markerList,
		isLocationChanged,
		isLocationSearchForOffice,
		setLastSelected,
		zoomToLevel,
		dispatch,
	]);
};
